// Whale trades list widget. Body = just the virtualized list. The sort
// toggle, filter dropdown, count, and coin picker all live in the
// WidgetFrame header via the exported `WhalesHeader` named export — page
// wires `body: <WhalesWidgetInstance .../>` + `headerExtras: <WhalesHeader .../>`
// in widgetProps. Both subscribe to the same useWidgetSetting state so
// changing sort from the header reflects in the body immediately.

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { sortWhales } from '../../../hooks/useSortModes';
import { useWidgetSetting } from '../cockpitContext.jsx';
import { useCoinTrades } from '../data/useCoinTrades';
import { formatPrice } from '../data/sharedLivePrices';
import {
  VirtualList, WhaleTradeRow, FilterDropdown, SortToggle, FILTER_OPTIONS,
} from '../../CoinTerminalSections/sectionHelpers';
import CoinPickerHeader from './CoinPickerHeader';

function WhalesWidgetInstance({ instanceId, defaultCoin, onOpenTrade }) {
  const { t } = useTranslation();
  const [coin] = useWidgetSetting(instanceId, 'coin', defaultCoin);
  const [whaleFilter] = useWidgetSetting(instanceId, 'filter', 0);
  const [sortMode] = useWidgetSetting(instanceId, 'sort', 'time');
  const { whaleTrades } = useCoinTrades(coin);

  const whales = useMemo(() => {
    const filtered = whaleFilter > 0 ? whaleTrades.filter((tr) => tr.dollarSize >= whaleFilter) : whaleTrades;
    return sortWhales(filtered, sortMode);
  }, [whaleTrades, whaleFilter, sortMode]);

  if (whales.length === 0) {
    return <div className="ct-section-content"><div className="ct-empty">{t('chartPage.empty.whaleTrades', 'No trades matching filter')}</div></div>;
  }
  return (
    <VirtualList
      items={whales}
      rowHeight={48}
      getKey={(tr, i) => tr._id || `${tr.timestamp}:${tr.exchange}:${tr.price}:${i}`}
      renderRow={(trade) => (
        <WhaleTradeRow
          trade={trade}
          coin={coin}
          formatPrice={formatPrice}
          longLabel={t('chartPage.side.long', 'LONG')}
          shortLabel={t('chartPage.side.short', 'SHORT')}
          onOpen={() => onOpenTrade?.(trade)}
        />
      )}
    />
  );
}

export const WhalesHeader = React.memo(function WhalesHeader({ instanceId, defaultCoin }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useWidgetSetting(instanceId, 'filter', 0);
  const [sortMode, setSortMode] = useWidgetSetting(instanceId, 'sort', 'time');
  const toggleSort = () => setSortMode((m) => (m === 'time' ? 'size' : 'time'));
  return (
    <>
      <SortToggle
        mode={sortMode}
        onToggle={toggleSort}
        labelTime={t('chartPage.sortByTime', 'Sort by time')}
        labelSize={t('chartPage.sortBySize', 'Sort by size')}
      />
      <FilterDropdown icon="whale" ariaLabel="Whale size filter" tooltip="Фильтр по размеру китовой сделки" options={FILTER_OPTIONS.whale} value={filter} onChange={setFilter} />
      <CoinPickerHeader instanceId={instanceId} defaultCoin={defaultCoin} />
    </>
  );
});

export default React.memo(WhalesWidgetInstance);
